/**
 * Offline Brain — used when `.env.local` still has the placeholder Supabase
 * URL/anon key, so there's no real proxy to call. Generates a canned
 * `draft_session_plan` that matches the same shape the real Anthropic tool
 * call would emit, so the approve/dismiss UI in Home renders end-to-end
 * exactly like the live path. Keyword-driven so a coach typing
 * "crossing" / "pressing" / "1v1" gets a thematically relevant draft.
 *
 * Closes the Phase 8 Layer-3 → Layer-2 loop locally:
 *   - gatherOfflinePriors() reads recent player goals + growth check-ins
 *     from the same localStorage stores the Layer-3 surfaces wrote to.
 *   - synthesizeOfflineDraft() weaves a one-liner referencing those priors
 *     into Brain's reply text so coaches can SEE that what they wrote in
 *     Reviews / Players is being weighted.
 *
 * When real env values land in `.env.local`, `isPlaceholderSupabase()`
 * returns false and the live path takes over — nothing in this file is
 * exercised in production.
 */

import { loadLocalGrowthCheckins, loadLocalPlayerGoals, type LocalPlayerGoal } from './demoData';

export interface OfflineBrainResult {
  text: string;
  draft: {
    type: 'draft_session_plan' | 'draft_tactics_pattern';
    payload: Record<string, unknown>;
  };
}

export interface OfflinePriors {
  /** Most-recent player goal across the coach's roster. */
  recentGoal?: { playerName: string | null; focus: string };
  /** Most-recent "wants help with" line from a growth check-in. */
  recentHelpWith?: string;
  /** Most-recent "wrestling with" line from a growth check-in. */
  recentWrestlingWith?: string;
  /** Total prior count surfaced — used so the UI can flag how many are weighted. */
  priorCount: number;
}

interface PhasePreset {
  title: string;
  primary: 'attacking' | 'defending' | 'transitions' | 'individual';
  intent: string;
  phaseCoverage: { attacking: number; defending: number; transitions: number; individual: number };
  cornerCoverage: { technical_tactical: number; physical: number; psychological: number; social: number };
  drills: Array<{
    name: string;
    description: string;
    drill_type: 'WARM_UP' | 'TECHNICAL' | 'TACTICAL' | 'SMALL_SIDED_GAME' | 'COOL_DOWN';
    primary_phase: 'attacking' | 'defending' | 'transitions' | 'individual';
    duration_mins: number;
    coaching_points: string[];
  }>;
}

const PRESETS: Record<string, PhasePreset> = {
  crossing: {
    title: 'Wide overload → crossing finishes',
    primary: 'attacking',
    intent: 'Stay brave on the touchline — defenders trust the inside runs',
    phaseCoverage: { attacking: 60, defending: 10, transitions: 20, individual: 10 },
    cornerCoverage: { technical_tactical: 55, physical: 20, psychological: 15, social: 10 },
    drills: [
      {
        name: 'Crossing arc warm-up',
        description: 'Pairs cross from cones at the corner of each penalty area into a teammate moving across the six-yard box.',
        drill_type: 'WARM_UP',
        primary_phase: 'individual',
        duration_mins: 10,
        coaching_points: ['Plant foot beside the ball', 'Whip across the keeper', 'Front-post run before back-post'],
      },
      {
        name: '4v4 wide overload',
        description: 'Two channels along each touchline are 2v1 in the attacker\'s favour. Goal counts double from a cross-led finish.',
        drill_type: 'SMALL_SIDED_GAME',
        primary_phase: 'attacking',
        duration_mins: 18,
        coaching_points: ['Overlap timing', 'Near-post / back-post / penalty-spot triangle', 'Recovery sprint after losing it'],
      },
      {
        name: '8v8 + 2 wingers conditioned game',
        description: 'Full game with one neutral winger per side who must stay wide. Goals from a cross worth 2.',
        drill_type: 'TACTICAL',
        primary_phase: 'attacking',
        duration_mins: 22,
        coaching_points: ['Switch the play to find the underloaded winger', 'Striker waits then attacks the cross late'],
      },
    ],
  },
  pressing: {
    title: 'Press triggers in the middle third',
    primary: 'defending',
    intent: 'Lead the press with your voice — your teammates need to hear the trigger',
    phaseCoverage: { attacking: 15, defending: 55, transitions: 25, individual: 5 },
    cornerCoverage: { technical_tactical: 45, physical: 25, psychological: 20, social: 10 },
    drills: [
      {
        name: 'Shadow press warm-up',
        description: 'Coach plays a ball into a defender; players shadow-press as a unit on a verbal trigger.',
        drill_type: 'WARM_UP',
        primary_phase: 'defending',
        duration_mins: 8,
        coaching_points: ['Trigger = back foot or heavy touch', 'Closest player presses, others squeeze'],
      },
      {
        name: '6v6+3 press game',
        description: 'Three neutrals support the team in possession. Defending team scores by winning the ball + 3 passes.',
        drill_type: 'SMALL_SIDED_GAME',
        primary_phase: 'transitions',
        duration_mins: 18,
        coaching_points: ['Don\'t press alone', 'Cut the angle into the centre', 'Set the trap to the touchline'],
      },
      {
        name: '8v8 mid-block to high press',
        description: 'Team defends a mid-block; on a coach trigger word ("up"), shifts into a high press for 30 seconds.',
        drill_type: 'TACTICAL',
        primary_phase: 'defending',
        duration_mins: 24,
        coaching_points: ['Compactness front-to-back', 'Striker leads, midfielders read', 'Recover the line on a switch'],
      },
    ],
  },
  '1v1': {
    title: '1v1 attacking & defending duels',
    primary: 'individual',
    intent: 'Show courage on the ball and trust the next decision',
    phaseCoverage: { attacking: 30, defending: 30, transitions: 10, individual: 30 },
    cornerCoverage: { technical_tactical: 50, physical: 25, psychological: 15, social: 10 },
    drills: [
      {
        name: 'Cone-gate dribble warm-up',
        description: 'Players dribble through a series of small gates with a defender shadowing.',
        drill_type: 'WARM_UP',
        primary_phase: 'individual',
        duration_mins: 8,
        coaching_points: ['Close control', 'Eyes up between touches'],
      },
      {
        name: '1v1 to mini-goal',
        description: 'Attacker receives at halfway and goes 1v1 to a small goal. Reset every 8 seconds.',
        drill_type: 'TECHNICAL',
        primary_phase: 'individual',
        duration_mins: 18,
        coaching_points: ['First touch attacks the space', 'Defender side-on, patient', 'Commit the defender then release'],
      },
      {
        name: '4v4 with 1v1 starts',
        description: 'Every 90 seconds, all four pairs reset into a 1v1 from a coach pass. Open play resumes after the duel.',
        drill_type: 'SMALL_SIDED_GAME',
        primary_phase: 'attacking',
        duration_mins: 20,
        coaching_points: ['Support arrives second', 'Lose well — sprint to recover'],
      },
    ],
  },
  passing: {
    title: 'Possession & 3rd-man passing',
    primary: 'attacking',
    intent: 'Communicate before you receive — who, where, when',
    phaseCoverage: { attacking: 50, defending: 15, transitions: 25, individual: 10 },
    cornerCoverage: { technical_tactical: 55, physical: 15, psychological: 10, social: 20 },
    drills: [
      {
        name: 'Diamond passing warm-up',
        description: 'Four-cone diamond, two-touch, rotation after every pass.',
        drill_type: 'WARM_UP',
        primary_phase: 'individual',
        duration_mins: 10,
        coaching_points: ['Open body shape', 'Pass with the inside', 'Call early'],
      },
      {
        name: 'Rondo 5v2 with 3rd-man rule',
        description: 'Possession 5v2 — outside players must combine via a third-man pass to score a point.',
        drill_type: 'TECHNICAL',
        primary_phase: 'attacking',
        duration_mins: 15,
        coaching_points: ['Set, spin, receive', 'Body positioning before the pass arrives'],
      },
      {
        name: '8v8 possession game',
        description: 'Goals are scored by stringing six passes that include a third-man combination.',
        drill_type: 'SMALL_SIDED_GAME',
        primary_phase: 'attacking',
        duration_mins: 20,
        coaching_points: ['Constantly scan', 'Receive on the half-turn'],
      },
    ],
  },
  default: {
    title: 'Balanced 4-3-3 session',
    primary: 'attacking',
    intent: 'Stay positive on the ball and reset together when we lose it',
    phaseCoverage: { attacking: 35, defending: 25, transitions: 25, individual: 15 },
    cornerCoverage: { technical_tactical: 45, physical: 20, psychological: 15, social: 20 },
    drills: [
      {
        name: 'Possession warm-up',
        description: '4v2 rondo in tight grids, two-touch limit.',
        drill_type: 'WARM_UP',
        primary_phase: 'individual',
        duration_mins: 10,
        coaching_points: ['Body shape', 'Communication'],
      },
      {
        name: 'Phase of play 6v4',
        description: 'Build from the back, attack a goal with keeper. Defenders score by playing into a halfway target.',
        drill_type: 'TACTICAL',
        primary_phase: 'attacking',
        duration_mins: 20,
        coaching_points: ['First pass forward', 'Width creates the gap', 'Striker drops + spinner runs'],
      },
      {
        name: '8v8 conditioned game',
        description: 'Full game with a 4-pass rule before scoring. Goals worth 2 if the move starts in our own third.',
        drill_type: 'SMALL_SIDED_GAME',
        primary_phase: 'attacking',
        duration_mins: 20,
        coaching_points: ['Patience in build', 'Trust the next pass'],
      },
    ],
  },
};

function pickPreset(query: string): PhasePreset {
  const q = query.toLowerCase();
  if (q.includes('cross')) return PRESETS.crossing;
  if (q.includes('press')) return PRESETS.pressing;
  if (q.includes('1v1') || q.includes('duel') || q.includes('one v one')) return PRESETS['1v1'];
  if (q.includes('pass') || q.includes('possession')) return PRESETS.passing;
  return PRESETS.default;
}

function nextSessionDateIso(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().slice(0, 10);
}

const PLAYERS_KEY = '4cm_local_players';

interface LocalPlayerNameRow { id: string; name: string }

function namesByPlayerId(): Map<string, string> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(PLAYERS_KEY) : null;
    const rows = raw ? (JSON.parse(raw) as LocalPlayerNameRow[]) : [];
    return new Map(rows.map((p) => [p.id, p.name]));
  } catch { return new Map(); }
}

function focusFromGoal(goal: LocalPlayerGoal): string | null {
  // The fact text was built as "• Focus: <text>\n• Evidence...".
  const m = goal.fact.match(/Focus:\s*(.+)/i);
  return m ? m[1].trim() : null;
}

function helpFromCheckin(answers: Array<{ questionId: string; answer: string }>): string | null {
  const help = answers.find((a) => a.questionId === 'help')?.answer?.trim();
  return help || null;
}

function wrestlingFromCheckin(answers: Array<{ questionId: string; answer: string }>): string | null {
  const w = answers.find((a) => a.questionId === 'wrestling')?.answer?.trim();
  return w || null;
}

/**
 * Gather Layer-3 priors the offline Brain should weight on the next reply.
 * Reads localStorage stores the Layer-3 surfaces wrote to (player goals,
 * growth check-ins). No-op if coachId is missing or stores are empty.
 */
export function gatherOfflinePriors(coachId: string | null | undefined): OfflinePriors {
  const out: OfflinePriors = { priorCount: 0 };
  if (!coachId) return out;

  // Most-recent goal across all players.
  const playerNames = namesByPlayerId();
  let mostRecentGoal: LocalPlayerGoal | null = null;
  for (const [, name] of playerNames) void name; // settle the type
  for (const playerId of playerNames.keys()) {
    const goals = loadLocalPlayerGoals(coachId, playerId);
    for (const g of goals) {
      if (!mostRecentGoal || g.created_at > mostRecentGoal.created_at) {
        mostRecentGoal = g;
      }
    }
  }
  if (mostRecentGoal) {
    const focus = focusFromGoal(mostRecentGoal);
    if (focus) {
      out.recentGoal = {
        playerName: mostRecentGoal.player_name ?? playerNames.get(mostRecentGoal.player_id) ?? null,
        focus,
      };
      out.priorCount += 1;
    }
  }

  // Most-recent growth check-in.
  const checkins = loadLocalGrowthCheckins(coachId, 1);
  if (checkins.length > 0) {
    const help = helpFromCheckin(checkins[0].answers);
    const wrestling = wrestlingFromCheckin(checkins[0].answers);
    if (help) {
      out.recentHelpWith = help;
      out.priorCount += 1;
    }
    if (wrestling) {
      out.recentWrestlingWith = wrestling;
      out.priorCount += 1;
    }
  }

  return out;
}

/** Build the human-readable "weighing N priors" sentence. */
export function describePriors(priors: OfflinePriors): string {
  if (priors.priorCount === 0) return '';
  const parts: string[] = [];
  if (priors.recentHelpWith) {
    parts.push(`your check-in ask "${priors.recentHelpWith}"`);
  }
  if (priors.recentGoal) {
    const who = priors.recentGoal.playerName ?? 'a player';
    parts.push(`${who}'s goal "${priors.recentGoal.focus}"`);
  }
  if (priors.recentWrestlingWith && parts.length < 2) {
    parts.push(`what your team is wrestling with: "${priors.recentWrestlingWith}"`);
  }
  if (parts.length === 0) return '';
  return `Weighing ${parts.join(' and ')}.`;
}

export function synthesizeOfflineDraft(
  query: string,
  teamId: string | null,
  priors: OfflinePriors = { priorCount: 0 },
): OfflineBrainResult {
  const preset = pickPreset(query);
  const totalDuration = preset.drills.reduce((s, d) => s + d.duration_mins, 0);
  const payload: Record<string, unknown> = {
    team_id: teamId ?? undefined,
    date: nextSessionDateIso(),
    title: preset.title,
    duration_mins: totalDuration,
    objectives: preset.drills.flatMap((d) => d.coaching_points).slice(0, 4),
    psych_social_intent: preset.intent,
    phase_coverage: preset.phaseCoverage,
    corner_coverage: preset.cornerCoverage,
    drills: preset.drills.map((d) => ({
      name: d.name,
      description: d.description,
      drill_type: d.drill_type,
      primary_phase: d.primary_phase,
      duration_mins: d.duration_mins,
      coaching_points: d.coaching_points,
    })),
  };

  const priorsLine = describePriors(priors);
  const text = [
    `Drafted "${preset.title}" — ${totalDuration} minutes, ${preset.drills.length} drills.`,
    `Primary phase is ${preset.primary}.`,
    priorsLine,
    'Review below and approve when it looks right.',
    '(Brain is in offline-stub mode — no real backend is configured.)',
  ].filter(Boolean).join(' ');

  return { text, draft: { type: 'draft_session_plan', payload } };
}

/**
 * Tactics-aware offline synth. Triggered by the Brain only when the
 * screen context is a tactics surface. Produces a canned 4-3-3 high
 * press layout + a 3-point press-trigger arrow so coaches running on
 * a fresh install (no Anthropic key, placeholder Supabase) can still
 * exercise the approve → board-updates loop end-to-end.
 */
export function synthesizeOfflineTacticsDraft(
  query: string,
  persistKey: string | null,
): OfflineBrainResult {
  const wantsPress = /press|trigger/i.test(query);
  const wantsLowBlock = /low block|defend deep|park the bus/i.test(query);

  // Default home formation: 4-3-3 in own half, GK at +z.
  const homeSpots = [
    { role: 'GK', x: 0, z: 30 },
    { role: 'LB', x: -20, z: 20 },
    { role: 'CB', x: -8, z: 22 },
    { role: 'CB', x: 8, z: 22 },
    { role: 'RB', x: 20, z: 20 },
    { role: 'CM', x: -10, z: 8 },
    { role: 'CM', x: 0, z: 5 },
    { role: 'CM', x: 10, z: 8 },
    { role: 'LW', x: -22, z: -8 },
    { role: 'ST', x: 0, z: -15 },
    { role: 'RW', x: 22, z: -8 },
  ];

  // If the coach asks for a press, push the front line + RW forward
  // and add a press-trigger arrow from the RB.
  if (wantsPress) {
    homeSpots[10] = { role: 'RW', x: 22, z: -18 }; // push higher
    homeSpots[9] = { role: 'ST', x: 0, z: -22 };
  }
  if (wantsLowBlock) {
    // Drop the back line + mids deeper, narrow the block.
    homeSpots[1] = { role: 'LB', x: -14, z: 28 };
    homeSpots[2] = { role: 'CB', x: -6, z: 30 };
    homeSpots[3] = { role: 'CB', x: 6, z: 30 };
    homeSpots[4] = { role: 'RB', x: 14, z: 28 };
    homeSpots[5] = { role: 'CM', x: -8, z: 18 };
    homeSpots[6] = { role: 'CM', x: 0, z: 18 };
    homeSpots[7] = { role: 'CM', x: 8, z: 18 };
  }

  const arrows = wantsPress
    ? [
        {
          points: [
            { x: 20, z: 20 },
            { x: 14, z: 0 },
            { x: 8, z: -10 },
          ],
          color: '#4ade80',
          player_key: 'home-4', // RB (0-indexed, position 4)
        },
      ]
    : [];

  const intent = wantsPress
    ? 'High press triggered from the right back'
    : wantsLowBlock
      ? 'Compact low block — 5-3-2 shape on own half'
      : '4-3-3 resting shape';

  const payload: Record<string, unknown> = {
    persist_key: persistKey ?? undefined,
    home_formation: wantsLowBlock ? '5-3-2' : '4-3-3',
    away_formation: '4-3-3',
    home_spots: homeSpots,
    arrows,
    intent,
  };

  const text = [
    `Drafted: ${intent}.`,
    `Review the preview — drag tokens after approve to refine.`,
    '(Brain is in offline-stub mode — no real backend is configured.)',
  ].join(' ');

  return { text, draft: { type: 'draft_tactics_pattern', payload } };
}

